import React, { createContext, useEffect, useState } from "react"

import { useAuth0 } from "@auth0/auth0-react"
import { useMsal } from "@azure/msal-react"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"

import {
  linkGoogleCalendar,
  newOutlookLink,
  resetSingleCalendar,
  unlinkExternalCalendar,
} from "@actions/calendarActions"
import { loginProviders } from "@config/enums"
import { loginRequest as msalLoginRequest } from "@config/msalConfig"
import {
  selectPrimaryGoogleCalendar,
  selectPrimaryOutlookCalendar,
  selectSyncEmail,
  selectDisplayCalendar,
} from "@selectors/calendar"
import { selectCalendarSyncSettings, selectUserInfo } from "@selectors/user"
import {
  changeSyncCalendar,
  fetchBackendConnectedCalendars,
  fetchCalendarEvents,
  setGoogleAccessToken,
  undisplayRemovedCalendars,
  unlinkCalendarEvents,
} from "@utils/calendarSyncUtils"
import { CALENDAR_TYPES } from "@utils/calendarUtils"
import { getExternalCalendars } from "@utils/getExternalCalendars"

import {
  useLinkingSuccessMessage,
  useLinkingModal,
  useCalendarControl,
  useSyncLinkedCalendar,
  useLinkingError,
} from "./hooks"

export const CalendarSettingsContext = createContext({})

const CalendarSettingsState = ({
  children,
  userInfo,
  googlePrimaryCalendar,
  outlookPrimaryCalendar,
  resetSingleCalendar,
  syncEmail,
  calendarSyncSettings,
  calendarDisplay,
}) => {
  console.log("googlePrimaryCalendar", googlePrimaryCalendar)
  const { getAccessTokenWithPopup } = useAuth0()
  const { instance: msalInstance } = useMsal()
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isOutlookLoading, setIsOutlookLoading] = useState(false)
  const [linkingSuccessType, setLinkingSuccessType] = useState(null)
  const [unlinkLoadingType, setUnlinkLoadingType] = useState(null)

  useEffect(() => {
    // Wait for the user to be authenticated before fetching the calendar events
    setTimeout(() => {
      fetchCalendarEvents({})
      // Wave 2 calendar-web: on dev, also pull the REAL backend-connected-calendars list
      // (GET /users/calendars) so the settings screen renders actual connected calendars /
      // write-target state instead of relying solely on a live Google/MS OAuth session.
      fetchBackendConnectedCalendars()
    }, 0)
  }, [])

  const getAccessToken = ({ connection, connection_scope }) => {
    return getAccessTokenWithPopup({
      connection,
      connection_scope,
      // CALFIX-CAL2 (2026-07-03, QJ ruling): "Add calendar" must let a second (or third) Google
      // account be connected, not silently re-consent the same one -- "select_account" forces
      // Google's own account-chooser screen before the consent screen every time, so a user with
      // multiple Google logins can pick a DIFFERENT one on each Add-calendar click. Auth0 passes
      // an unrecognized prompt value straight through to the upstream Google connection; combined
      // with "consent" (space-separated, both OIDC-valid) Google still also re-shows the scope
      // consent screen for THIS account if it hasn't already granted the calendar scope.
      prompt: "select_account consent",
      access_type: "offline",
    })
      .then(res => res)
      .catch(() => {
        switch (connection) {
          case loginProviders.google:
            setIsGoogleLoading(false)
            break
          case loginProviders.windowslive:
            setIsOutlookLoading(false)
            break
          default:
            break
        }

        setLinkingError("User canceled the operation.")
      })
  }

  const { setSyncingType } = useSyncLinkedCalendar({
    syncEmail,
    googlePrimaryCalendar,
    outlookPrimaryCalendar,
    selectedCalendars: calendarSyncSettings?.selectedCalendars,
  })

  const { catchLinkingErrorMessage } = useLinkingError()

  const handleAddGoogleCalendar = async event => {
    event.preventDefault()
    setIsGoogleLoading(true)

    // Wave 2b Google-cal e2e fix: MUST use the dedicated Auth0 "google-calendar" social
    // connection (client 527311205414-...apps.googleusercontent.com, project pa-contacts-fetch --
    // the one QJ added the QA persona as a test user on), NOT the generic login connection
    // (loginProviders.google = "google-oauth2", client 359458481299-... which has no test users
    // and is a different, unrelated GCP project). The backend (api/routers/calendars.py
    // _google_calendars/_google_token_from_identity) already looks for a "google-calendar"
    // identity first (falling back to google-oauth2 only if absent) and its GOOGLE_CALENDAR_
    // CLIENT_ID/SECRET refresh creds match the 527311205414 client -- so this was a client/
    // backend contract mismatch: the button silently authorized against the wrong Google client.
    const accessToken = await getAccessToken({
      connection: "google-calendar",
      connection_scope: "https://www.googleapis.com/auth/calendar",
    })
    if (!accessToken) return

    try {
      await linkGoogleCalendar(accessToken)

      setSyncingType(loginProviders.google)
      fetchCalendarEvents({
        isGoogleLinking: true,
      })
      fetchBackendConnectedCalendars()
      setIsLinkingModal(false)
      setIsGoogleLoading(false)
      setLinkingSuccessType(loginProviders.google)
    } catch (error) {
      const errorMessage = catchLinkingErrorMessage(error)

      setLinkingError(errorMessage)
      setIsLinkingModal(false)
      setIsGoogleLoading(false)
    }
  }

  const handleAddMSCalendar = async event => {
    event.preventDefault()
    setIsOutlookLoading(true)

    // Wave 2b Outlook-cal e2e fix: the OLD path drove Auth0's own `windowslive` SOCIAL
    // CONNECTION (client 178a7bea..., but Auth0's windowslive strategy hard-routes to
    // login.live.com/consumers -- personal Microsoft/MSA accounts ONLY. That connection is
    // ALSO enabled on the shared Teeup-web SPA client (prod login-with-Microsoft uses the same
    // connection), so its tenant routing could not be changed in place without risking prod
    // login. Any real CooWe customer using an Outlook/Office 365 CALENDAR has a Microsoft 365
    // work/school (Azure AD) account, which login.live.com/consumers categorically rejects
    // ("We couldn't find a Microsoft account") -- confirmed live 2026-07-02, see
    // DEV-TO-QA-WAVE2B-OUTLOOK-CAL.md.
    //
    // Fix: use the app's OWN pre-existing client-side MSAL popup flow (already fully wired at
    // the provider level in src/index.js -- `msalInstance`/`MsalProvider` -- and pre-configured
    // in src/config/msalConfig.js with `authority: "https://login.microsoftonline.com/common/"`,
    // which accepts BOTH personal AND Azure AD work/school accounts) instead of Auth0's
    // windowslive connection. This talks directly to Microsoft, never touches Auth0's windowslive
    // connection or its prod-shared config, and reuses the SAME Azure app registration
    // (178a7bea-91f6-4bc4-939f-424d6f3c7f95) Auth0 itself uses under the hood -- so no new Azure
    // app registration or Auth0 connection was created; only the button's identity path changed.
    let accessToken
    try {
      const result = await msalInstance.loginPopup(msalLoginRequest)
      accessToken = result?.accessToken
    } catch (msalError) {
      console.error("MSAL Outlook calendar linking failed", msalError)
      setIsOutlookLoading(false)
      setLinkingError("User canceled the operation.")
      return
    }

    if (!accessToken) {
      setIsOutlookLoading(false)
      setLinkingError("User canceled the operation.")
      return
    }

    try {
      await newOutlookLink(accessToken)

      setSyncingType(loginProviders.windowslive)
      fetchBackendConnectedCalendars()
      setIsLinkingModal(false)
      setIsOutlookLoading(false)
      setLinkingSuccessType(loginProviders.windowslive)
    } catch (error) {
      const errorMessage = catchLinkingErrorMessage(error)

      setLinkingError(errorMessage)
      setIsLinkingModal(false)
      setIsOutlookLoading(false)
    }
  }

  const unsyncCalendar = isSynced => {
    if (isSynced) {
      changeSyncCalendar(null)
    }
  }

  const handleRemoveCalendar = async () => {
    const { calendars } = userInfo || {}
    const { googleCalendar, outlookCalendar } = getExternalCalendars(calendars)
    const { calendarId, calendarType } = unlinkCalendar
    await undisplayRemovedCalendars(calendarType)
    const isSynced =
      syncEmail?.calendarEmailId === unlinkCalendar.calendarId &&
      syncEmail?.calendarType === unlinkCalendar.calendarType

    setUnlinkLoadingType(calendarType)
    unsyncCalendar(isSynced)

    switch (calendarType) {
      case CALENDAR_TYPES.google:
        await unlinkExternalCalendar(googleCalendar.id)
        setGoogleAccessToken(null)
        resetSingleCalendar({ calendarId, calendarType: CALENDAR_TYPES.google })
        break
      case CALENDAR_TYPES.outlook:
        await unlinkExternalCalendar(outlookCalendar.id)
        await unlinkCalendarEvents(calendarId)

        resetSingleCalendar({
          calendarId,
          calendarType: CALENDAR_TYPES.outlook,
        })
        break
    }

    fetchBackendConnectedCalendars()
    setUnlinkLoadingType(null)
  }

  const { linkingSuccessMessage, setLinkingSuccessMessage } =
    useLinkingSuccessMessage({
      linkingSuccessType,
      googlePrimaryCalendar,
      outlookPrimaryCalendar,
    })

  const addCalendarControl = useCalendarControl({
    googlePrimaryCalendar,
    outlookPrimaryCalendar,
  })

  const closeModals = () => {
    setIsLinkingModal(false)
    setLinkingError("")
    setLinkingSuccessMessage("")
    setLinkingSuccessType(null)
    setUnlinkCalendar(null)
  }

  const {
    setIsLinkingModal,
    linkingModalData,
    linkingError,
    setLinkingError,
    unlinkCalendar,
    setUnlinkCalendar,
  } = useLinkingModal({
    linkingSuccessMessage,
    closeModals,
    handleRemoveCalendar,
  })

  return (
    <CalendarSettingsContext.Provider
      value={{
        setIsLinkingModal,
        isGoogleLoading,
        isOutlookLoading,
        linkingError,
        linkingSuccessMessage,
        linkingModalData,
        googlePrimaryCalendar,
        outlookPrimaryCalendar,
        unlinkCalendar,
        unlinkLoadingType,
        addCalendarControl,
        setLinkingError,
        handleAddGoogleCalendar,
        handleAddMSCalendar,
        closeModals,
        setUnlinkCalendar,
      }}>
      {children}
    </CalendarSettingsContext.Provider>
  )
}

const mapStateToProps = state => ({
  userInfo: selectUserInfo(state),
  googlePrimaryCalendar: selectPrimaryGoogleCalendar(state),
  outlookPrimaryCalendar: selectPrimaryOutlookCalendar(state),
  syncEmail: selectSyncEmail(state),
  calendarSyncSettings: selectCalendarSyncSettings(state),
  calendarDisplay: selectDisplayCalendar(state),
})

const mapDispatchToProps = dispatch => ({
  resetSingleCalendar: bindActionCreators(resetSingleCalendar, dispatch),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CalendarSettingsState)
